import { AvatarGroupV2, AvatarV2, DataGrid, Progress, TooltipV2 } from '@cimplico/components';
import { LockClosedIcon, ArchiveBoxIcon, CheckBadgeIcon, CheckIcon, ExclamationTriangleIcon } from '@heroicons/react/24/solid';
import { Link } from 'react-router-dom';
import { useFirmContext } from '../../providers/FirmProvider';
import { useUserContext } from '../../providers/UserProvider';
import { useIsFirmRole } from '../../hooks/use-is-firm-role';
import { Workpaper, WorkpaperRoleEnum, WorkpaperStatusEnum } from '../../types/workpaper';
import { formatDateTime, calcRelativeTime, formatDate, formatMixedCase } from '../../utilities/formatting';
import plural from '../../utilities/plural';

type WorkpaperRowProps = {
    id: string;
    workpaper: Workpaper;
};

function WorkpaperRow({ id, workpaper }: WorkpaperRowProps): JSX.Element {
    const { shortId } = useFirmContext();
    const { id: userId } = useUserContext();
    const { isAdmin } = useIsFirmRole();

    const roleOrder = [WorkpaperRoleEnum.APPROVER, WorkpaperRoleEnum.REVIEWER, WorkpaperRoleEnum.PREPARER, WorkpaperRoleEnum.GUEST];
    const isAssigned = workpaper.assigned.find((a) => a.id === userId);
    const canAccess = isAdmin ? true : workpaper.private ? isAssigned : true;
    const badge = (
        <div className="bg-green-600 rounded-full p-0.5">
            <CheckIcon className="w-1.5 h-1.5 text-white" />
        </div>
    );

    return (
        <DataGrid.Row id={id} disabled={!canAccess}>
            <div className="flex items-center gap-2">
                <section>
                    <div className="flex items-center gap-2">
                        {canAccess ? (
                            <Link to={`/${shortId}/workpapers/${workpaper.id}/dashboard`}>
                                <p className="font-medium text-indigo-600 dark:text-indigo-400 text-sm cursor-pointer">{workpaper.name}</p>
                            </Link>
                        ) : (
                            <p className="font-medium text-gray-600 dark:text-indigo-400 text-sm">{workpaper.name}</p>
                        )}
                        {workpaper.status === WorkpaperStatusEnum.COMPLETE && (
                            <TooltipV2 message="This workpaper is complete.">
                                <CheckBadgeIcon className="h-4 w-4 text-gray-600" />
                            </TooltipV2>
                        )}
                        {workpaper.status === WorkpaperStatusEnum.ARCHIVED && (
                            <TooltipV2 message="This workpaper is archived.">
                                <ArchiveBoxIcon className="h-4 w-4 text-gray-600" />
                            </TooltipV2>
                        )}
                        {workpaper.private && (
                            <TooltipV2 message="This workpaper is private.">
                                <LockClosedIcon className="h-4 w-4 text-gray-600" />
                            </TooltipV2>
                        )}
                    </div>
                    <label className="text-gray-500 text-sm line-clamp-1">{formatDate(new Date(workpaper.periodEnd))}</label>
                </section>
            </div>
            <p className="text-sm text-gray-700">{workpaper.entity.name}</p>
            <TooltipV2 message={formatDateTime(workpaper.updatedAt)}>
                <p className="text-gray-700 text-sm">{calcRelativeTime(workpaper.updatedAt)}</p>
            </TooltipV2>

            <div className="shrink-0">
                <AvatarGroupV2 size="xs">
                    {workpaper.assigned
                        .sort((a, b) => roleOrder.indexOf(a.role) - roleOrder.indexOf(b.role))
                        .sort((a) => (a.certified ? -1 : 1))
                        .map((assignee) => {
                            return (
                                <AvatarV2
                                    key={assignee.id || assignee.email}
                                    src={assignee.avatarUrl}
                                    alt={assignee.firstName && assignee.lastName ? assignee.firstName[0] + assignee.lastName[0] : undefined}
                                    color={assignee.profileHexColour}
                                    badge={assignee.certified && badge}
                                    tooltip={`${
                                        assignee.firstName && assignee.lastName
                                            ? `${assignee.firstName} ${assignee.lastName}`
                                            : assignee.email
                                    } (${formatMixedCase(assignee.role)})`}
                                />
                            );
                        })}
                </AvatarGroupV2>
            </div>
            {workpaper.status === WorkpaperStatusEnum.ACTIVE && (
                <TooltipV2 message={formatDate(workpaper.dueDate)}>
                    <div className="flex items-center gap-1">
                        {workpaper.overdue ? (
                            <>
                                <ExclamationTriangleIcon className="h-4 w-4 text-red-600" />
                                <p className="text-sm text-red-600 font-medium">
                                    {workpaper.overdueDays || 0} {plural(workpaper.overdueDays || 0, 'day')} ago
                                </p>
                            </>
                        ) : (
                            <p className="text-gray-700 text-sm">{calcRelativeTime(new Date(workpaper.dueDate))}</p>
                        )}
                    </div>
                </TooltipV2>
            )}
            {workpaper.status !== WorkpaperStatusEnum.ACTIVE && (
                <TooltipV2 message={formatDateTime(workpaper.dueDate)}>
                    <p className="text-green-600 text-sm">{calcRelativeTime(new Date(workpaper.dueDate))}</p>
                </TooltipV2>
            )}
            <div className="flex items-center">
                <Progress percent={workpaper.progressPercent} showProgressLabel type="circle" />
            </div>
        </DataGrid.Row>
    );
}
export default WorkpaperRow;
